/**
 * Fällige Einnahmen: lesen und quittieren — lokal zuerst.
 *
 * Quittieren muss offline funktionieren. Der typische Moment ist morgens in der
 * Küche mit schlechtem Empfang: Wenn der Haken dort nicht sitzt, setzt ihn
 * niemand später nach, und die App fasst weiter nach, obwohl längst genommen
 * wurde.
 */

import i18n from 'i18next';
import {
  addDays,
  describeAmount,
  intakeKey,
  newId,
  planIntakes,
  wallTimeToUtc,
} from '@kraftwerk/shared';
import type { MealSlot } from '@kraftwerk/shared';
import { db } from './local.js';
import type { KraftwerkDatabase, LocalIntake, LocalSupplement } from './local.js';

export type { LocalIntake };

export interface DueIntake {
  id: string;
  supplementId: string;
  supplementName: string;
  dose: string | null;
  dueAt: Date;
  acknowledgedAt: Date | null;
  skippedAt: Date | null;
  /** Zur Mahlzeit, falls gebunden — für die Verbindung zum Tagesprotokoll. */
  mealSlot: MealSlot | null;
  /**
   * Noch nicht vom Server geplant, nur aus dem Zeitplan errechnet: steht schon
   * da, hat aber noch keine Zeile zum Abhaken. Kommt die echte Einnahme an,
   * ersetzt sie diese Vorschau.
   */
  preview?: boolean;
}

/**
 * „1 Stück“ statt „1 piece“ (18.10.2026): Seit #91 steht die Einheit als
 * Schlüssel da; ausgeschrieben wird sie wie im Push über `describeAmount`,
 * in der Sprache der App. Freitext älterer Supplements bleibt, wie er ist.
 */
function doseLabel(dose: number | null, unit: string | null): string | null {
  if (dose === null) return null;
  return describeAmount(dose, unit, i18n.language?.startsWith('en') ? 'en' : 'de');
}

/**
 * Soll die Einnahme verschwinden, weil ihr Supplement gelöscht oder pausiert ist?
 *
 * Offene verschwinden immer: Der Scheduler zieht sie ohnehin zurück, bis zum
 * nächsten Takt und Abgleich sollen sie aber nicht mehr dastehen.
 *
 * `fuerHeute`: „Heute" zeigt nur, was jetzt dran ist — ein gelöschtes Supplement
 * gehört dort auch abgehakt nicht mehr hin (Geräteabnahme 10.10.2026). Das
 * Tagesprotokoll dagegen behält Abgehaktes und Übersprungenes: Das ist die
 * Geschichte des Tages.
 */
function withdrawnLocally(
  intake: LocalIntake,
  byId: Map<string, LocalSupplement>,
  fuerHeute: boolean,
): boolean {
  const supplement = byId.get(intake.supplementId);
  // Unbekannt heißt: noch nicht abgeglichen — dann lieber zeigen.
  if (!supplement) return false;
  if (fuerHeute && supplement.deletedAt) return true;
  if (intake.acknowledgedAt || intake.skippedAt) return false;
  return Boolean(supplement.deletedAt) || supplement.active === false;
}

/**
 * Die Einnahmen eines Kalendertages, aufsteigend nach Uhrzeit.
 *
 * Der Tag wird über die tatsächlichen Zeitpunkte begrenzt, nicht über einen
 * String-Vergleich — sonst schneidet die Zeitzone den Tag an der falschen Stelle.
 */
export async function listIntakesForDay(
  day: Date,
  database: KraftwerkDatabase = db,
  /** Mit Zeitzone kommen noch nicht geplante Einnahmen als Vorschau dazu. */
  previewTimeZone: string | null = null,
): Promise<DueIntake[]> {
  const start = new Date(day);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);

  const [intakes, supplements] = await Promise.all([
    database.supplement_intakes.toArray(),
    database.supplements.toArray(),
  ]);

  const byId = new Map(supplements.map((entry) => [entry.id, entry]));

  const real = intakes
    .filter((intake) => !intake.deletedAt && !withdrawnLocally(intake, byId, true))
    .map((intake) => ({ intake, dueAt: new Date(intake.dueAt) }))
    .filter(({ dueAt }) => dueAt >= start && dueAt < end)
    .map(({ intake, dueAt }) => {
      const supplement = byId.get(intake.supplementId);
      return {
        id: intake.id,
        supplementId: intake.supplementId,
        supplementName: supplement?.name ?? '—',
        dose: supplement ? doseLabel(supplement.dose, supplement.unit) : null,
        dueAt,
        acknowledgedAt: intake.acknowledgedAt ? new Date(intake.acknowledgedAt) : null,
        skippedAt: intake.skippedAt ? new Date(intake.skippedAt) : null,
        mealSlot: supplement?.mealSlot ?? null,
      };
    });

  const preview = previewTimeZone
    ? previewIntakes(supplements, intakes, previewTimeZone, start, end)
    : [];

  return [...real, ...preview].sort((a, b) => a.dueAt.getTime() - b.dueAt.getTime());
}

/**
 * Was der Scheduler für heute noch planen wird — mit **derselben** Funktion
 * (`planIntakes`), damit App und Telefon nicht auseinanderlaufen.
 *
 * Bis zum 12.10.2026 stand ein frisch angelegtes Supplement bis zu anderthalb
 * Minuten nicht auf „Heute": Einnahmen legt nur der Server an. Angelegt wird
 * hier weiterhin nichts — eine zweite Stelle, die Einnahmen erzeugt, hätte
 * Einnahmen ohne Erinnerung zur Folge.
 */
function previewIntakes(
  supplements: readonly LocalSupplement[],
  intakes: readonly LocalIntake[],
  timeZone: string,
  start: Date,
  end: Date,
): DueIntake[] {
  const live = supplements.filter((entry) => !entry.deletedAt && entry.active);
  const existing = new Set(
    intakes
      .filter((intake) => !intake.deletedAt)
      .map((intake) => intakeKey(intake.supplementId, new Date(intake.dueAt))),
  );
  const byId = new Map(live.map((entry) => [entry.id, entry]));

  return planIntakes({
    supplements: live.map((entry) => ({
      id: entry.id,
      name: entry.name,
      dose: entry.dose,
      unit: entry.unit,
      active: entry.active,
      schedule: { times: entry.scheduleTimes, weekdays: entry.weekdays, months: entry.months },
    })),
    timeZone,
    from: new Date(),
    horizonDays: 0,
    existing,
  })
    .filter((item) => item.dueAt >= start && item.dueAt < end)
    .map((item) => {
      const supplement = byId.get(item.supplementId);
      return {
        id: intakeKey(item.supplementId, item.dueAt),
        supplementId: item.supplementId,
        supplementName: item.supplementName,
        dose: supplement ? doseLabel(supplement.dose, supplement.unit) : null,
        dueAt: item.dueAt,
        acknowledgedAt: null,
        skippedAt: null,
        mealSlot: supplement?.mealSlot ?? null,
        preview: true,
      };
    });
}

/**
 * Die Einnahmen eines **Kalendertages in der Zeitzone des Nutzers** — für das
 * Tagesprotokoll, das ebenfalls in Kalendertagen denkt.
 *
 * `listIntakesForDay` begrenzt nach der Uhr des Geräts; im Protokoll wäre das
 * auf Reisen der falsche Tag. Hier gelten dieselben Grenzen wie für die
 * Mahlzeiten daneben.
 */
export async function listIntakesForDate(
  date: string,
  timeZone: string,
  database: KraftwerkDatabase = db,
): Promise<DueIntake[]> {
  const start = wallTimeToUtc(date, '00:00', timeZone);
  const end = wallTimeToUtc(addDays(date, 1), '00:00', timeZone);
  const intakes = await database.supplement_intakes.toArray();
  const supplements = await database.supplements.toArray();
  const byId = new Map(supplements.map((entry) => [entry.id, entry]));

  return intakes
    .filter((intake) => !intake.deletedAt && !withdrawnLocally(intake, byId, false))
    .map((intake) => ({ intake, dueAt: new Date(intake.dueAt) }))
    .filter(({ dueAt }) => dueAt >= start && dueAt < end)
    .map(({ intake, dueAt }) => {
      const supplement = byId.get(intake.supplementId);
      return {
        id: intake.id,
        supplementId: intake.supplementId,
        supplementName: supplement?.name ?? '—',
        dose: supplement ? doseLabel(supplement.dose, supplement.unit) : null,
        dueAt,
        acknowledgedAt: intake.acknowledgedAt ? new Date(intake.acknowledgedAt) : null,
        skippedAt: intake.skippedAt ? new Date(intake.skippedAt) : null,
        mealSlot: supplement?.mealSlot ?? null,
      };
    })
    .sort((a, b) => a.dueAt.getTime() - b.dueAt.getTime());
}

async function markIntake(
  id: string,
  changes: Partial<Pick<LocalIntake, 'acknowledgedAt' | 'skippedAt' | 'skipReason'>>,
  database: KraftwerkDatabase,
): Promise<void> {
  const now = new Date().toISOString();

  await database.transaction('rw', [database.supplement_intakes, database.outbox], async () => {
    const existing = await database.supplement_intakes.get(id);
    if (!existing) return;

    const row: LocalIntake = { ...existing, ...changes, updatedAt: now };
    await database.supplement_intakes.put(row);
    await database.outbox.add({
      mutationId: newId(),
      table: 'supplement_intakes',
      kind: 'upsert',
      row,
      createdAt: now,
      attempts: 0,
    });
  });
}

/** Genommen. Beendet die Nachfass-Kette, sobald der Server es erfährt. */
export function acknowledgeIntake(id: string, database: KraftwerkDatabase = db): Promise<void> {
  return markIntake(id, { acknowledgedAt: new Date().toISOString(), skippedAt: null }, database);
}

/**
 * Bewusst ausgelassen — mit Grund.
 *
 * Ein „heute nicht, weil nichts da" ist etwas anderes als Vergessen. Ohne diese
 * Unterscheidung müsste man entweder lügen (Haken setzen) oder sich weiter nerven
 * lassen; beides bringt Leute dazu, die Erinnerungen ganz abzuschalten.
 */
export function skipIntake(
  id: string,
  reason: string | null,
  database: KraftwerkDatabase = db,
): Promise<void> {
  return markIntake(
    id,
    { skippedAt: new Date().toISOString(), skipReason: reason, acknowledgedAt: null },
    database,
  );
}
